import React, { useState, useRef, useEffect } from 'react'
import { Sparkles, X, Loader2, Send, Maximize2, Minimize2 } from 'lucide-react'
import { supabase } from '../lib/supabase'

interface Message {
  role: 'user' | 'assistant'
  content: string
}

const QUICK_SUGGESTIONS = [
  '📊 Quanti professionisti abbiamo?',
  '📋 Colloqui da seguire questa settimana?',
  '⚠️ Polizze assicurative in scadenza?',
  '🗺️ Distribuzione per regione?',
  '📝 Contratti mancanti?',
]

export default function GlobalAiCopilot() {
  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const endRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, isOpen])

  // Expanded context fetching — full data access
  const fetchGlobalContext = async () => {
    try {
      const { data: nurses } = await supabase.from('nurses').select('nome, cognome, regione, provincia, contratto_firmato, tipo_contratto, assicurazione_scadenza, qualifica, partita_iva, opi_provincia, opi_numero')
      const { data: interviews } = await supabase.from('interviews').select('nome, cognome, stato, data_colloquio, data_follow_up, tipo_contratto, regione')

      const now = new Date()
      const prossima_settimana = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000)

      const totale_nurses = nurses?.length || 0
      const contratti_firmati = nurses?.filter(n => n.contratto_firmato).length || 0
      const contratti_mancanti = totale_nurses - contratti_firmati
      
      const polizze_scadute = nurses?.filter(n => {
        if (!n.assicurazione_scadenza) return false
        return new Date(n.assicurazione_scadenza) < now
      }).length || 0
      const polizze_in_scadenza_30gg = nurses?.filter(n => {
        if (!n.assicurazione_scadenza) return false
        const scad = new Date(n.assicurazione_scadenza)
        return scad >= now && scad <= new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000)
      }).length || 0

      const per_regione: Record<string, number> = {}
      nurses?.forEach(n => { per_regione[n.regione] = (per_regione[n.regione] || 0) + 1 })

      const colloqui_totali = interviews?.length || 0
      const colloqui_in_attesa = interviews?.filter(i => i.stato === 'in_attesa').length || 0
      const colloqui_idonei = interviews?.filter(i => i.stato === 'idoneo').length || 0
      const follow_up_imminenti = interviews?.filter(i => {
        if (!i.data_follow_up) return false
        const d = new Date(i.data_follow_up)
        return d >= now && d <= prossima_settimana
      }) || []

      const senza_piva = nurses?.filter(n => !n.partita_iva).length || 0

      return {
        professionisti: {
          totale: totale_nurses,
          contratti_firmati,
          contratti_mancanti,
          senza_partita_iva: senza_piva,
          distribuzione_regionale: per_regione,
        },
        assicurazioni: {
          scadute: polizze_scadute,
          in_scadenza_30_giorni: polizze_in_scadenza_30gg,
          nomi_scadute: nurses?.filter(n => n.assicurazione_scadenza && new Date(n.assicurazione_scadenza) < now).map(n => `${n.cognome} ${n.nome}`).slice(0, 10),
        },
        colloqui: {
          totali: colloqui_totali,
          in_attesa: colloqui_in_attesa,
          idonei: colloqui_idonei,
          follow_up_prossimi_7gg: follow_up_imminenti.map(i => ({ nome: `${i.cognome} ${i.nome}`, data: i.data_follow_up, stato: i.stato })),
        },
        lista_professionisti: nurses?.map(n => ({ nome: `${n.cognome} ${n.nome}`, regione: n.regione, provincia: n.provincia, contratto: n.contratto_firmato ? 'firmato' : 'mancante', tipo: n.tipo_contratto || 'non specificato' })).slice(0, 50),
      }
    } catch {
      return "Errore nel prelevare il contesto dal database."
    }
  }

  const handleSend = async (text?: string) => {
    const userText = (text || input).trim()
    if (!userText || loading) return
    setInput('')
    setMessages(prev => [...prev, { role: 'user', content: userText }])
    setLoading(true)

    const apiKey = localStorage.getItem('GEMINI_API_KEY')
    if (!apiKey) {
      setMessages(prev => [...prev, { role: 'assistant', content: '❌ API Key Gemini non trovata. Inseriscila nelle Impostazioni!' }])
      setLoading(false)
      return
    }

    try {
      const globalData = await fetchGlobalContext()
      const systemPrompt = `Sei "QuiCare A.I.", l'assistente direzionale intelligente integrato nel gestionale QuiCare Srl per la gestione di infermieri e professionisti sanitari.

CONTESTO REAL-TIME DAL DATABASE:
${JSON.stringify(globalData, null, 1)}

REGOLE:
- Rispondi in italiano, in modo professionale ma amichevole
- Usa i dati reali forniti sopra per rispondere con precisione
- Puoi fare calcoli, percentuali, confronti sui dati
- Se ti chiedono nomi specifici, cerca nei dati forniti
- Per azioni che richiedono modifiche al database (cambiare stati, aggiornare dati), spiega che al momento puoi solo consultare i dati e indica in quale sezione della piattaforma possono fare la modifica
- Non inventare dati. Se non hai l'informazione, dillo
- Massimo 250 parole per risposta`

      const convoHistory = messages.slice(-6).map(m => ({ 
        role: m.role === 'user' ? 'user' : 'model', 
        parts: [{ text: m.content }] 
      }))

      const payload = {
        contents: [
          { role: 'user', parts: [{ text: systemPrompt }] },
          { role: 'model', parts: [{ text: "Ciao! Sono QuiCare A.I., il tuo assistente direzionale. Ho accesso completo ai dati del gestionale: professionisti, colloqui, contratti, assicurazioni. Come posso aiutarti?" }] },
          ...convoHistory,
          { role: 'user', parts: [{ text: userText }] }
        ]
      }

      const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })

      if (!response.ok) { let eMsg = `Network error (${response.status})`; try { const eData = await response.json(); eMsg += `: ${eData.error?.message}`; } catch(e){} throw new Error(eMsg); }
      const data = await response.json()
      const aiResponse = data.candidates?.[0]?.content?.parts?.[0]?.text || 'Nessuna risposta generata.'
      
      setMessages(prev => [...prev, { role: 'assistant', content: aiResponse.replace(/\*/g, '') }])
    } catch (error) {
      setMessages(prev => [...prev, { role: 'assistant', content: "⚠️ Errore di connessione a Gemini A.I." }])
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    handleSend()
  }

  return (
    <>
      <div className="fixed bottom-6 right-6 z-[100] flex flex-col items-end">
        {isOpen && (
          <div className={`${expanded ? 'fixed inset-4 md:inset-x-24 md:inset-y-12 rounded-3xl' : 'w-[400px] h-[560px] mb-4 rounded-3xl'} overflow-hidden shadow-2xl transition-all duration-300 transform origin-bottom-right border border-indigo-200/50 flex flex-col`}
               style={{ 
                 background: 'linear-gradient(145deg, rgba(255,255,255,0.95), rgba(240,245,255,0.9))',
                 backdropFilter: 'blur(20px)',
                 boxShadow: '0 25px 50px -12px rgba(79, 70, 229, 0.25)' 
               }}>
            
            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-4 flex items-center justify-between text-white shadow-md relative z-10">
              <div className="flex items-center gap-3">
                <div className="bg-white/20 p-2 rounded-xl backdrop-blur-md">
                  <Sparkles className="w-5 h-5 text-indigo-100" />
                </div>
                <div>
                  <h3 className="font-black text-lg leading-tight tracking-tight">QuiCare A.I.</h3>
                  <div className="text-[10px] text-indigo-200 font-bold uppercase tracking-widest flex items-center gap-1 mt-0.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse"></span> Copilot Direzionale
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => setExpanded(!expanded)} className="p-2 hover:bg-white/10 rounded-lg transition-colors text-indigo-100">
                  {expanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                </button>
                <button onClick={() => setIsOpen(false)} className="p-2 hover:bg-red-500/20 rounded-lg transition-colors text-indigo-100 hover:text-red-100">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 relative scroll-smooth">
              {messages.length === 0 && (
                <div className="h-full flex flex-col items-center justify-center text-center px-4 space-y-4">
                  <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-500 mb-2 shadow-inner">
                    <Sparkles className="w-8 h-8" />
                  </div>
                  <h4 className="text-slate-700 font-black text-lg">Come posso aiutarti?</h4>
                  <p className="text-xs text-slate-500 max-w-[280px]">
                    Ho accesso ai dati di professionisti, colloqui, contratti e assicurazioni. Chiedimi qualsiasi cosa!
                  </p>
                  {/* Quick suggestions */}
                  <div className="flex flex-wrap gap-2 justify-center mt-2">
                    {QUICK_SUGGESTIONS.map((s, i) => (
                      <button key={i} onClick={() => handleSend(s.replace(/^[^\s]+\s/, ''))} className="text-xs px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-full border border-indigo-200 transition-colors font-medium">
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {messages.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] rounded-2xl p-3.5 shadow-sm text-sm ${
                    msg.role === 'user' 
                      ? 'bg-gradient-to-br from-indigo-500 to-indigo-600 text-white rounded-br-none' 
                      : 'bg-white text-slate-700 border border-slate-100 rounded-bl-none shadow-indigo-100/50'
                  }`}>
                    {msg.role === 'assistant' && (
                      <div className="flex items-center gap-1.5 mb-1 opacity-50">
                        <Sparkles className="w-3 h-3 text-indigo-500" />
                        <span className="text-[10px] font-black uppercase tracking-wider text-indigo-600">A.I.</span>
                      </div>
                    )}
                    <div className="leading-relaxed whitespace-pre-line">{msg.content}</div>
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-white border border-slate-100 rounded-2xl p-4 rounded-bl-none shadow-sm flex items-center gap-2">
                    <div className="flex gap-1">
                      <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-bounce [animation-delay:-0.3s]"></span>
                      <span className="w-1.5 h-1.5 bg-purple-500 rounded-full animate-bounce [animation-delay:-0.15s]"></span>
                      <span className="w-1.5 h-1.5 bg-pink-500 rounded-full animate-bounce"></span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={endRef} className="h-1" />
            </div>

            {/* Input Bar */}
            <div className="p-3 bg-white/50 backdrop-blur-md border-t border-slate-200/50">
              <form onSubmit={handleSubmit} className="relative flex items-center">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Chiedi al Copilot..."
                  className="w-full bg-white/80 border border-slate-200 text-slate-800 text-sm rounded-full pl-4 pr-12 py-3.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent shadow-inner transition-all"
                  disabled={loading}
                />
                <button
                  type="submit"
                  disabled={!input.trim() || loading}
                  className="absolute right-1.5 p-2.5 bg-indigo-600 text-white rounded-full hover:bg-indigo-700 disabled:opacity-50 disabled:hover:bg-indigo-600 transition-all shadow-md"
                >
                  <Send className="w-4 h-4" />
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Floating Toggle Button */}
        {!isOpen && (
          <button 
            onClick={() => setIsOpen(true)}
            className="group relative flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white shadow-[0_0_30px_rgba(99,102,241,0.5)] hover:shadow-[0_0_40px_rgba(99,102,241,0.7)] hover:scale-105 transition-all duration-300"
          >
            <div className="absolute inset-0 rounded-full bg-white opacity-0 group-hover:opacity-20 transition-opacity"></div>
            <Sparkles className="w-7 h-7 relative z-10 group-hover:animate-pulse" />
            <span className="absolute w-full h-full rounded-full border border-indigo-400 animate-ping opacity-20"></span>
          </button>
        )}
      </div>
    </>
  )
}
